/**
 * File utility - replacement for react-jhipster file functions
 * Handles file operations like opening files and setting file data
 */

/**
 * Opens a file in a new window
 * @param contentType - MIME type of the file
 * @param data - File data (base64 or blob)
 */
export function openFile(contentType: string, data: string): void {
  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
    // IE workaround
    const byteCharacters = atob(data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {type: contentType});
    (window.navigator as any).msSaveOrOpenBlob(blob);
  } else {
    // Modern browsers
    const fileURL = `data:${contentType};base64,${data}`;
    window.open(fileURL);
  }
}

const MAX_LOGO_WIDTH = 1200;
const JPEG_QUALITY = 0.85;
// Must match backend ImgService.computeHorizontalThumbnail dimensions
const HORIZONTAL_THUMBNAIL_WIDTH = 540;
const HORIZONTAL_THUMBNAIL_HEIGHT = 185;
// Must match backend ImgService.computeSquareThumbnail dimensions
const SQUARE_THUMBNAIL_SIZE = 185;

type CompressedImage = {
  contentType: string;
  base64Data: string;
  horizontalThumbnailBase64: string;
  squareThumbnailBase64: string;
};

/**
 * Compresses an image file via canvas resize + JPEG conversion.
 * All formats (JPEG, PNG, SVG, etc.) are rasterized to JPEG for
 * consistent rendering in @react-pdf/renderer and smaller payloads.
 * Also generates the horizontal (540x185, contain, left-aligned, white bg)
 * and square (fit-inside 185x185, white bg) thumbnails so the local preview
 * stays coherent before the next refetch — both match the backend
 * ImgService.regenerateLogoThumbnails output.
 */
export function compressImageFile(file: File): Promise<CompressedImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const base64Data = rasterizeToJpeg(img, MAX_LOGO_WIDTH);
        const horizontalThumbnailBase64 = computeHorizontalThumbnail(img);
        const squareThumbnailBase64 = computeSquareThumbnail(img);
        resolve({
          contentType: "image/jpeg",
          base64Data,
          horizontalThumbnailBase64,
          squareThumbnailBase64,
        });
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

/** Resize image to fit maxWidth, keeping aspect ratio, and convert to JPEG base64. */
function rasterizeToJpeg(img: HTMLImageElement, maxWidth: number): string {
  let {width, height} = img;
  if (width > maxWidth) {
    height = Math.round(height * (maxWidth / width));
    width = maxWidth;
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return "";
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY).split(",")[1] || "";
}

/** Generate a 540x185 thumbnail matching backend sharp(fit:contain, position:left, bg:white). */
function computeHorizontalThumbnail(img: HTMLImageElement): string {
  const canvas = document.createElement("canvas");
  canvas.width = HORIZONTAL_THUMBNAIL_WIDTH;
  canvas.height = HORIZONTAL_THUMBNAIL_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return "";
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, HORIZONTAL_THUMBNAIL_WIDTH, HORIZONTAL_THUMBNAIL_HEIGHT);

  // fit: "contain", position: "left" — scale to fit, left-aligned
  const scale = Math.min(
    HORIZONTAL_THUMBNAIL_WIDTH / img.width,
    HORIZONTAL_THUMBNAIL_HEIGHT / img.height
  );
  const drawWidth = Math.round(img.width * scale);
  const drawHeight = Math.round(img.height * scale);
  const y = Math.round((HORIZONTAL_THUMBNAIL_HEIGHT - drawHeight) / 2);
  ctx.drawImage(img, 0, y, drawWidth, drawHeight);

  return canvas.toDataURL("image/jpeg", JPEG_QUALITY).split(",")[1] || "";
}

/** Generate a thumbnail fit inside 185x185 (white bg) matching backend sharp(fit:inside). */
function computeSquareThumbnail(img: HTMLImageElement): string {
  const scale = Math.min(SQUARE_THUMBNAIL_SIZE / img.width, SQUARE_THUMBNAIL_SIZE / img.height);
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return "";
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", JPEG_QUALITY).split(",")[1] || "";
}

const PHOTO_MAX_WIDTH = 1600;
const PHOTO_JPEG_QUALITY = 0.75;

export type PhotoCompressionOptions = {
  maxLongSide?: number;
  targetBytes?: number;
  maxBytes?: number;
  minLongSide?: number;
  minQuality?: number;
};

export function compressPhotoFile(
  file: File,
  maxWidthOrOptions: number | PhotoCompressionOptions = PHOTO_MAX_WIDTH
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          resolve(compressLoadedPhoto(img, maxWidthOrOptions));
        } catch (error) {
          reject(error);
        }
      };
      img.onerror = () =>
        reject(new Error("Photo illisible. Choisissez un JPEG ou reprenez la photo."));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error("Impossible de lire la photo."));
    reader.readAsDataURL(file);
  });
}

function compressLoadedPhoto(
  img: HTMLImageElement,
  maxWidthOrOptions: number | PhotoCompressionOptions
): string {
  if (!img.width || !img.height) {
    throw new Error("La photo ne possède pas de dimensions valides.");
  }
  const options = typeof maxWidthOrOptions === "number" ? undefined : maxWidthOrOptions;
  const maxDimension =
    options?.maxLongSide ??
    (typeof maxWidthOrOptions === "number" ? maxWidthOrOptions : PHOTO_MAX_WIDTH);
  const initialDimension = options ? Math.max(img.width, img.height) : img.width;
  const scale = Math.min(1, maxDimension / initialDimension);
  let width = Math.max(1, Math.round(img.width * scale));
  let height = Math.max(1, Math.round(img.height * scale));
  const minimumDimension = Math.min(Math.max(width, height), options?.minLongSide ?? 960);
  const minimumQuality = options?.minQuality ?? 0.5;
  const maxBytes = options?.maxBytes ?? Number.POSITIVE_INFINITY;
  const targetBytes = Math.min(options?.targetBytes ?? maxBytes, maxBytes);
  if (
    !(maxDimension > 0) ||
    !(minimumDimension > 0) ||
    minimumQuality <= 0 ||
    minimumQuality > PHOTO_JPEG_QUALITY ||
    maxBytes <= 0 ||
    targetBytes <= 0
  ) {
    throw new Error("Paramètres de compression invalides.");
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Impossible de préparer la photo sur cet appareil.");
  }
  let acceptableResult: string | undefined;
  while (true) {
    canvas.width = width;
    canvas.height = height;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    let quality = PHOTO_JPEG_QUALITY;
    while (true) {
      const result = canvas.toDataURL("image/jpeg", quality);
      if (!result.startsWith("data:image/jpeg;base64,")) {
        throw new Error("La conversion JPEG a échoué.");
      }
      const base64 = result.slice("data:image/jpeg;base64,".length);
      const bytes =
        (base64.length * 3) / 4 - (base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0);
      if (bytes <= 0) {
        throw new Error("La conversion JPEG a échoué.");
      }
      if (bytes <= targetBytes) {
        return result;
      }
      if (bytes <= maxBytes) {
        acceptableResult = result;
      }
      if (quality === minimumQuality) {
        break;
      }
      quality = Math.max(minimumQuality, Math.round((quality - 0.075) * 1000) / 1000);
    }
    const currentDimension = Math.max(width, height);
    if (currentDimension <= minimumDimension) {
      break;
    }
    const nextScale =
      Math.max(minimumDimension, Math.floor(currentDimension * 0.85)) / currentDimension;
    width = Math.max(1, Math.round(width * nextScale));
    height = Math.max(1, Math.round(height * nextScale));
  }
  if (acceptableResult) {
    return acceptableResult;
  }
  throw new Error(
    "Cette photo reste trop volumineuse. Choisissez une autre photo ou reprenez-la."
  );
}

/**
 * Sets file data from a file input event
 * @param event - File input change event
 * @param callback - Callback function that receives content type and base64 data
 * @param isImage - Whether the file is an image
 */
export function setFileData(
  event: React.ChangeEvent<HTMLInputElement>,
  callback: (contentType: string, base64Data: string) => void,
  isImage: boolean
): void {
  if (event.target.files?.[0]) {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = (readerEvent: ProgressEvent<FileReader>) => {
      if (readerEvent.target?.result) {
        let base64Data: string;

        if (isImage && typeof readerEvent.target.result === "string") {
          // For readAsDataURL, extract base64 from data URL
          const dataUrl = readerEvent.target.result;
          base64Data = dataUrl.split(",")[1] || "";
        } else if (typeof readerEvent.target.result === "string") {
          // For readAsBinaryString
          base64Data = btoa(readerEvent.target.result);
        } else {
          base64Data = "";
        }

        callback(file.type, base64Data);
      }
    };

    if (isImage) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsBinaryString(file);
    }
  }
}
